import { useState, useEffect, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Receipt, RefreshCw, Eye, Send, Download, Filter, Mail, CheckCircle, XCircle, Clock
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

const BACKEND_URL = ((typeof window !== 'undefined' && window.location.hostname.includes('seatpilot.co.uk')) ? 'https://seatpilot.co.uk' : (process.env.REACT_APP_BACKEND_URL || 'https://seatpilot.co.uk'));
const API = `${BACKEND_URL}/api`;

export default function BackofficeInvoices() {
  const { theme } = useOutletContext();
  const isDark = theme === 'dark';

  const [invoices, setInvoices] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("invoices");

  useEffect(() => {
    fetchInvoices();
    fetchEmailLogs();
  }, []);
const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/invoices/`, { withCredentials: true });
      setInvoices(res.data.invoices || []);
    } catch (error) {
      console.error("Failed to fetch invoices:", error);
      toast.error("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchEmailLogs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/invoices/email-logs`, { withCredentials: true });
      setEmailLogs(res.data.logs || []);
    } catch (error) {
      console.error("Failed to fetch email logs:", error);
    }
  }, []);

  const sendInvoice = async (invoiceId) => {
    try {
      const res = await axios.post(`${API}/invoices/${invoiceId}/send`, {}, { withCredentials: true });
      toast.success(`Invoice sent to ${res.data.sent_to}`);
      fetchInvoices();
      fetchEmailLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invoice");
    }
  };

  const filteredInvoices = invoices.filter(i => filter === "all" || i.status === filter);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch(status) {
      case "paid": return "bg-green-100 text-green-700";
      case "overdue": return "bg-red-100 text-red-700";
      case "pending": return "bg-yellow-100 text-yellow-700";
      default: return "bg-gray-100 text-gray-700";
    }
  };

  // Stats
  const stats = {
    total: invoices.length,
    pending: invoices.filter(i => i.status === "pending").length,
    paid: invoices.filter(i => i.status === "paid").length,
    overdue: invoices.filter(i => i.status === "overdue").length,
    totalValue: invoices.reduce((sum, i) => sum + (i.total || 0), 0)
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Invoices</h1>
          <p className={`mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Manage customer invoices and payments</p>
        </div>
        <Button onClick={() => { fetchInvoices(); fetchEmailLogs(); }} variant="outline" size="sm" className="gap-2">
          <RefreshCw className="w-4 h-4" /> Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className={`p-4 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}`}>
          <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Total Invoices</p>
          <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{stats.total}</p>
        </Card>
        <Card className={`p-4 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}`}>
          <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Pending</p>
          <p className="text-2xl font-bold text-yellow-500">{stats.pending}</p>
        </Card>
        <Card className={`p-4 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}`}>
          <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Paid</p>
          <p className="text-2xl font-bold text-green-500">{stats.paid}</p>
        </Card>
        <Card className={`p-4 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}`}>
          <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Overdue</p>
          <p className="text-2xl font-bold text-red-500">{stats.overdue}</p>
        </Card>
        <Card className={`p-4 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}`}>
          <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Total Value</p>
          <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>£{stats.totalValue.toFixed(2)}</p>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className={`grid w-full grid-cols-2 ${isDark ? 'bg-slate-800' : 'bg-gray-100'}`}>
          <TabsTrigger value="invoices">
            <Receipt className="w-4 h-4 mr-2" /> All Invoices
          </TabsTrigger>
          <TabsTrigger value="email-logs">
            <Mail className="w-4 h-4 mr-2" /> Email Logs ({emailLogs.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="invoices">
          {/* Invoice List */}
          <Card className={`p-6 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>All Invoices</h3>
              <div className="flex items-center gap-2">
                <Filter className={`w-4 h-4 ${isDark ? 'text-slate-400' : 'text-gray-400'}`} />
                <Select value={filter} onValueChange={setFilter}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="overdue">Overdue</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin mx-auto text-gray-400" />
              </div>
            ) : filteredInvoices.length === 0 ? (
              <div className="text-center py-12">
                <Receipt className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-slate-600' : 'text-gray-300'}`} />
                <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>No invoices found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className={`border-b ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
                      <th className={`text-left py-3 px-4 text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Invoice #</th>
                      <th className={`text-left py-3 px-4 text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Customer</th>
                      <th className={`text-left py-3 px-4 text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Date</th>
                      <th className={`text-left py-3 px-4 text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Amount</th>
                      <th className={`text-left py-3 px-4 text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Status</th>
                      <th className={`text-left py-3 px-4 text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Sent</th>
                      <th className={`text-right py-3 px-4 text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInvoices.map(inv => (
                      <tr key={inv.invoice_id} className={`border-b ${isDark ? 'border-slate-700 hover:bg-slate-700/50' : 'border-gray-100 hover:bg-gray-50'}`}>
                        <td className={`py-3 px-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          <span className="font-mono font-medium">{inv.invoice_number}</span>
                        </td>
                        <td className={`py-3 px-4 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                          {inv.customer?.name || inv.company_id}
                        </td>
                        <td className={`py-3 px-4 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                          {formatDate(inv.created_at)}
                        </td>
                        <td className={`py-3 px-4 font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          £{inv.total?.toFixed(2)}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(inv.status)}`}>
                            {inv.status}
                          </span>
                        </td>
                        <td className={`py-3 px-4 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                          {inv.sent_at ? (
                            <span className="flex items-center gap-1 text-xs text-green-600">
                              <CheckCircle className="w-3 h-3" /> {formatDate(inv.sent_at)}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">Not sent</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex justify-end gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              title="View PDF"
                              onClick={() => window.open(`${API}/invoices/${inv.invoice_id}/pdf`, "_blank")}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              title="Download PDF"
                              onClick={() => window.open(`${API}/invoices/${inv.invoice_id}/pdf?download=true`, "_blank")}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            {inv.status !== "paid" && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                title="Send to Customer"
                                onClick={() => sendInvoice(inv.invoice_id)}
                              >
                                <Send className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="email-logs">
          {/* Email Logs */}
          <Card className={`p-6 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Invoice Email History
              </h3>
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                Shows both auto-sent and manual invoice emails
              </p>
            </div>

            {emailLogs.length === 0 ? (
              <div className="text-center py-12">
                <Mail className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-slate-600' : 'text-gray-300'}`} />
                <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>No email logs yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className={`border-b ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
                      <th className={`text-left py-3 px-4 text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Status</th>
                      <th className={`text-left py-3 px-4 text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Invoice</th>
                      <th className={`text-left py-3 px-4 text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Sent To</th>
                      <th className={`text-left py-3 px-4 text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Subject</th>
                      <th className={`text-left py-3 px-4 text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emailLogs.map(log => (
                      <tr key={log.log_id} className={`border-b ${isDark ? 'border-slate-700 hover:bg-slate-700/50' : 'border-gray-100 hover:bg-gray-50'}`}>
                        <td className="py-3 px-4">
                          {log.status === 'sent' ? (
                            <span className="flex items-center gap-1 text-green-600">
                              <CheckCircle className="w-4 h-4" />
                              <span className="text-xs">Sent</span>
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-red-500">
                              <XCircle className="w-4 h-4" />
                              <span className="text-xs">Failed</span>
                            </span>
                          )}
                        </td>
                        <td className={`py-3 px-4 font-mono text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {log.invoice_number}
                        </td>
                        <td className={`py-3 px-4 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                          <div>
                            <p className="text-sm">{log.to_email}</p>
                            {log.to_name && <p className="text-xs text-gray-500">{log.to_name}</p>}
                          </div>
                        </td>
                        <td className={`py-3 px-4 text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                          {log.subject}
                        </td>
                        <td className={`py-3 px-4 text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                          {formatDate(log.sent_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
